import type {
  CapopsComponent,
  CurvePhase,
  Logistics,
  LogisticsAssumptions,
  MaintenanceColor,
  MaintenanceFamily,
  MaintenancePhase,
  MaterielFamily,
  UnitCapops,
} from "./api/types";
import { k, t } from "./i18n/i18n";

/** Maintenance (chantier « DTO / CAPOPS ») : vocabulaire fermé de l'interface, seuils de couleur partagés avec le
 *  serveur (`generex_engine/maintenance.py`) et phrases composées (« CAPOPS 62 % : limitée par la DTO des chars et
 *  chenillés »). Module pur : il ne fait que présenter ce que le serveur calcule (`Logistics.maintenance`).
 *
 *  DTO : disponibilité technique opérationnelle, part des matériels techniquement disponibles ; indicateur logistique,
 *  jugé contre la DTO requise de son parc à chaque temps (hypothèse du dossier, réglée par curseurs parc × temps), avec
 *  ses propres couleurs (`dtoColor`). CAPOPS : capacité
 *  opérationnelle, aptitude de l'unité à remplir sa mission (maillon le plus faible du personnel, des matériels et des
 *  munitions), à ses propres couleurs (`maintenanceColor`). Les deux sigles restent à l'écran ; ils sont écrits en
 *  toutes lettres à leur première occurrence. */

/** Familles de matériel, dans l'ordre du serveur. */
export const FAMILIES: readonly MaterielFamily[] = ["tracked", "wheeled", "artillery", "engineer", "logistic", "drones"];

const FAMILY_LABELS: Record<MaterielFamily, string> = {
  tracked: k("Chars et chenillés"),
  wheeled: k("Blindés à roues"),
  artillery: k("Artillerie"),
  engineer: k("Génie"),
  logistic: k("Véhicules logistiques"),
  drones: k("Drones"),
};

/** Libellé d'une famille de matériel (« Chars et chenillés »). */
export function familyLabel(family: MaterielFamily): string {
  return t(FAMILY_LABELS[family]);
}

/** Sigles en toutes lettres, pour leur première occurrence dans une vue et l'aide. */
export const DTO_FULL = k("DTO (disponibilité technique opérationnelle)");
export const CAPOPS_FULL = k("CAPOPS (capacité opérationnelle)");

/** Temps de la courbe de DTO : T0 (départ, à l'heure H), puis la fin de chaque temps. */
export const CURVE_PHASES: readonly CurvePhase[] = ["T0", "T1", "T2", "T3"];

/** Seuils de couleur de la CAPOPS, comme le serveur (décision d'Alexis du 04/11/2026, plus bas que ceux de l'iPad) : vert
 *  au-dessus de 70 %, rouge à 50 % et en dessous. */
export const GREEN_ABOVE = 70;
export const RED_AT_MOST = 50;
/** CAPOPS de départ (T0) au plus, comme le serveur (`CAPOPS_START_MAX`) : les temps suivants partent de cette valeur. */
export const CAPOPS_START_MAX = 90;
/** DTO : orange de moins de 5 points sous la DTO requise, rouge plus bas (comme le serveur). */
export const DTO_ORANGE_POINTS = 5;

/** Temps jugés contre une DTO requise (T0 est la DTO de départ, jamais jugée). */
export const REQUIRED_PHASES: readonly MaintenancePhase[] = ["T1", "T2", "T3"];

/** Hypothèse « DTO de départ » d'un parc (`dto_start_tracked`). */
export function dtoStartKey(family: MaterielFamily): keyof LogisticsAssumptions {
  return `dto_start_${family}` as keyof LogisticsAssumptions;
}

/** Hypothèse « DTO requise » d'un parc à un temps (`dto_required_t2_tracked`). */
export function dtoRequiredKey(family: MaterielFamily, phase: MaintenancePhase): keyof LogisticsAssumptions {
  return `dto_required_${phase.toLowerCase()}_${family}` as keyof LogisticsAssumptions;
}

/** Les 24 hypothèses de DTO de départ et de DTO requise, parc par parc. */
export const FLEET_KEYS: readonly (keyof LogisticsAssumptions)[] = FAMILIES.flatMap((f) => [
  dtoStartKey(f),
  ...REQUIRED_PHASES.map((p) => dtoRequiredKey(f, p)),
]);

/** Couleur d'une CAPOPS arrondie à l'entier affiché ; null sans valeur. Jamais pour une DTO (`dtoColor`). */
export function maintenanceColor(percent: number | null | undefined): MaintenanceColor | null {
  if (percent == null || Number.isNaN(percent)) return null;
  const value = Math.round(percent);
  if (value > GREEN_ABOVE) return "green";
  if (value > RED_AT_MOST) return "orange";
  return "red";
}

/** Couleur d'une DTO face à la DTO `required` requise de son temps : conforme (vert) à la requise ou au-dessus — jamais
 *  une alerte —, orange de moins de 5 points en dessous, rouge plus bas ; null sans valeur ou sans requise (T0). */
export function dtoColor(percent: number | null | undefined, required: number | null | undefined): MaintenanceColor | null {
  if (percent == null || Number.isNaN(percent) || required == null) return null;
  const gap = Math.round(required) - Math.round(percent);
  if (gap <= 0) return "green";
  return gap < DTO_ORANGE_POINTS ? "orange" : "red";
}

/** DTO requise d'un parc à un temps ; null à T0 (DTO de départ). */
export function requiredOf(family: MaintenanceFamily, phase: CurvePhase): number | null {
  return phase === "T0" ? null : family.required[phase];
}

/** « DTO requise en T2 : 60 % ». */
export function requiredText(phase: MaintenancePhase, required: number): string {
  return t("DTO requise en {phase} : {n} %", { phase, n: Math.round(required) });
}

/** Lecture d'une DTO face à la requise : « conforme à la DTO requise » (à la requise ou au-dessus) ou « sous la DTO
 *  requise ». */
export function dtoReading(percent: number, required: number): string {
  return dtoColor(percent, required) === "green" ? t("conforme à la DTO requise") : t("sous la DTO requise");
}

/** Ton des composants partagés (`Badge`, `Callout`) pour une couleur. */
export function colorTone(color: MaintenanceColor | null): "ok" | "warn" | "danger" | "neutral" {
  if (color === "green") return "ok";
  if (color === "orange") return "warn";
  if (color === "red") return "danger";
  return "neutral";
}

/** Code du point de situation DIREX (`DirexCell.color` : 0 vert, 1 orange, 2 rouge ; −1 non évaluée). */
export function cellColor(color: MaintenanceColor | null): -1 | 0 | 1 | 2 {
  return color === "green" ? 0 : color === "orange" ? 1 : color === "red" ? 2 : -1;
}

/** Pourcentage affiché (« 62 % »), arrondi à l'entier. */
export function percentText(percent: number): string {
  return t("{n} %", { n: Math.round(percent) });
}

const LIMITS: Record<Exclude<CapopsComponent, "materiel">, string> = {
  personnel: k("limitée par le personnel"),
  munitions: k("limitée par les munitions"),
  depart: k("limitée par le potentiel de départ"),
};

/** « limitée par la DTO de… » : une phrase par famille (article et accord du français). */
const FAMILY_LIMITS: Record<MaterielFamily, string> = {
  tracked: k("limitée par la DTO des chars et chenillés"),
  wheeled: k("limitée par la DTO des blindés à roues"),
  artillery: k("limitée par la DTO de l'artillerie"),
  engineer: k("limitée par la DTO du génie"),
  logistic: k("limitée par la DTO des véhicules logistiques"),
  drones: k("limitée par la DTO des drones"),
};

/** « DTO des chars et chenillés » : une phrase par famille (article et accord du français). */
const FAMILY_DTO: Record<MaterielFamily, string> = {
  tracked: k("DTO des chars et chenillés"),
  wheeled: k("DTO des blindés à roues"),
  artillery: k("DTO de l'artillerie"),
  engineer: k("DTO du génie"),
  logistic: k("DTO des véhicules logistiques"),
  drones: k("DTO des drones"),
};

/** « DTO des chars et chenillés ». */
export function familyDto(family: MaterielFamily): string {
  return t(FAMILY_DTO[family]);
}

/** Ce qui limite une CAPOPS (« limitée par la DTO des chars et chenillés ») ; null quand rien ne la limite. */
export function limitText(capops: UnitCapops): string | null {
  if (!capops.limit) return null;
  if (capops.limit === "materiel") return capops.family ? t(FAMILY_LIMITS[capops.family]) : null;
  return t(LIMITS[capops.limit]);
}

/** Phrase complète : « CAPOPS 62 % : limitée par la DTO des chars et chenillés » (« CAPOPS 100 % » sans limite). */
export function capopsSentence(capops: UnitCapops): string {
  const head = t("CAPOPS {n} %", { n: Math.round(capops.capops) });
  const limit = limitText(capops);
  return limit ? t("{head} : {limit}", { head, limit }) : head;
}

/** CAPOPS d'une unité au temps affiché, lue dans la réponse de la logistique (la fiche d'unité partage sa requête) ;
 *  null en CENTAC, sans estimation ou pour une unité non posée. */
export function unitCapops(logistics: Logistics | undefined, key: string, phase: CurvePhase): UnitCapops | null {
  return logistics?.maintenance?.units[key]?.[phase] ?? null;
}
